import { ReactNode, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import ModalShell from './ModalShell'

type Props = {
  isOpen: boolean
  title: string
  description?: string
  maxWidthClassName?: string
  onClose: () => void
  children: ReactNode
}

export default function Modal({
  isOpen,
  title,
  description,
  maxWidthClassName = 'max-w-md',
  onClose,
  children,
}: Props) {
  useEffect(() => {
    if (!isOpen) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [isOpen, onClose])

  useEffect(() => {
    if (!isOpen || typeof document === 'undefined') return
    const previousBodyOverflow = document.body.style.overflow
    const previousHtmlOverflow = document.documentElement.style.overflow
    document.body.style.overflow = 'hidden'
    document.documentElement.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previousBodyOverflow
      document.documentElement.style.overflow = previousHtmlOverflow
    }
  }, [isOpen])

  // Track where mousedown started so we only close when both
  // mousedown AND mouseup happen on the backdrop (not dragged from inside)
  const mouseDownTarget = useRef<EventTarget | null>(null)

  if (!isOpen) return null

  const modal = (
    <div
      className="modal-backdrop fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onMouseDown={(e) => {
        mouseDownTarget.current = e.target
      }}
      onMouseUp={(e) => {
        if (e.target === e.currentTarget && mouseDownTarget.current === e.currentTarget) {
          onClose()
        }
        mouseDownTarget.current = null
      }}
      role="dialog"
      aria-modal="true"
    >
      <ModalShell className={`w-full ${maxWidthClassName} max-h-[95vh] sm:max-h-[90vh] flex flex-col`}>
        <div className="modal-header p-6 flex items-start justify-between gap-4 flex-shrink-0">
          <div className="flex-1">
            <h2 className="modal-title text-lg font-semibold">{title}</h2>
            {description ? <p className="modal-description text-sm mt-1">{description}</p> : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="modal-close flex-shrink-0"
            aria-label="Close modal"
          >
            ×
          </button>
        </div>
        <div className="px-6 pb-6 flex-1 overflow-y-auto">{children}</div>
      </ModalShell>
    </div>
  )

  return typeof document === 'undefined' ? null : createPortal(modal, document.body)
}

